import React from 'react';
import { motion } from 'framer-motion';

const ProcessSection = () => {
  const stages = [
    {
      title: "Start & Discover Phase",
      description:
        "Without a well-structured plan, the development of the application is worthless. Planning ascertains the strengths and weaknesses of a project, gives it a perfect start, and affects its growth positively.",
    },
    {
      title: "Requirement Analysis",
      description:
        "This step is about analyzing the recital of the application at different stages and creating remarks on add-on necessities. The analysis is considered to be very significant for ensuring that the project progresses in the right direction.",
    },
    {
      title: "Design Specification",
      description:
        "The next step after requirement analysis is designing, in which a structural design of the app is built. This step focuses on optimizing the usage of resources while ensuring to meet the time and budget specifications of the client.",
    },
    {
      title: "Development & Implementation",
      description:
        "The real task of the app development starts here with data recording going on at the backdrop. Once the application is developed, the implementation phase begins, followed by a pilot study of the complete app to check if each step functions appropriately.",
    },
    {
      title: "Testing & Debugging",
      description:
        "As the implementation phase ends, the product goes under testing where continuous debugging is done on various different test cases to identify errors and document bugs present in the application.",
    },
    {
      title: "Maintenance & Support",
      description:
        "This is a continuous and repetitive process that aims to identify the updates required by the app to meet the advancements and changes occurring in the market. Full dedicated support is also provided to the clients when required.",
    },
  ];

  // Container animation to stagger children
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Animation for heading and paragraph
  const textVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { type: "spring", stiffness: 100, damping: 20 } 
    },
  };

  // Animation for each card
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 120, damping: 15 },
    },
  };

  // Hover animation on card
  const cardHover = {
    scale: 1.05,
    boxShadow: "0px 10px 20px rgba(0,0,0,0.12)",
    transition: { type: "spring", stiffness: 300, damping: 20 },
  };

  // Pulse animation for step number circle
  const pulseVariants = {
    animate: {
      scale: [1, 1.1, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className="flex flex-col items-center justify-center p-2 lg:p-10 max-w-7xl mx-auto gap-y-10 text-center mt-5 lg:mt-10">
      {/* Animate heading and paragraph */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h1
          className="font-extrabold text-2xl lg:text-4xl text-gray-700"
          variants={textVariant}
        >
          Project Stages
        </motion.h1>
        <motion.p
          className="font-semibold text-xl lg:text-2xl mt-2 text-gray-700"
          variants={textVariant}
        >
          We follow a well-defined development procedure to take an application from ideation to actualization, aiming to provide innovative app
          development solutions to our clients.
        </motion.p>
      </motion.div>

      {/* Animated stages grid */}
      <motion.div
        className="grid grid-cols-1 relative md:grid-cols-2 xl:grid-cols-3 gap-3"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {stages.map((stage, index) => (
          <motion.div
            key={index}
            className="p-5 rounded-sm bg-white cursor-pointer"
            variants={cardVariants}
            whileHover={cardHover}
          >
            <div className="flex items-center justify-center mb-4">
              <div className="px-2 border rounded-full border-primaryColor relative">
                {/* Pulsing number circle */}
                <motion.div
                  className="text-2xl text-primaryColor rounded-full py-1 bg-white px-6 border-8 border-primaryColor my-2"
                  variants={pulseVariants}
                  animate="animate"
                >
                  {index + 1}
                </motion.div>

                <div className="border w-2 absolute -left-2 bg-white top-8 bottom-8 z-20 border-2 rounded-full p-1 border-primaryColor"></div>
                <div className="border w-2 absolute -right-2 bg-white top-8 bottom-8 z-20 border-2 rounded-full p-1 border-primaryColor"></div>
              </div>
            </div>
            <h1 className="text-xl font-bold text-gray-800 my-2">{stage.title}</h1>
            <p className="text-lg text-gray-700">{stage.description}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default ProcessSection;
